import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownFoldMenu } from '#/components/Dropdown/menus/Fold/DropdownFoldMenu';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { searchGetJobsRoutePath } from '#/modules/search/jobs/routing/searchGetJobsRoutePath';

import { findAJobDropdownDataByLocale } from './data/findAJobDropdownDataByLocale';
import {
  findAJobDropdownDataByResourcesItemType,
  findAJobDropdownDataBySearchItemType,
} from './findAJobDropdownConstants';

const triggerTextByLocale: Record<I18nLocale, { text: string; href: string }> =
  {
    en: {
      text: 'Find a Job',
      href: searchGetJobsRoutePath({
        locale: I18nLocale.EN,
        embedSearchWidgetId: null,
      }),
    },
    es: {
      text: 'Encuentra empleo',
      href: searchGetJobsRoutePath({
        locale: I18nLocale.ES,
        embedSearchWidgetId: null,
      }),
    },
    pt: {
      text: 'Buscar Emprego',
      href: searchGetJobsRoutePath({
        locale: I18nLocale.PT,
        embedSearchWidgetId: null,
      }),
    },
  };

export function FindAJobDropdown() {
  const { locale } = useI18n();
  const { text, href } = triggerTextByLocale[locale];
  const { search, resources } = findAJobDropdownDataByLocale[locale];

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          data={
            isDesktop
              ? {
                  type: 'link',
                  href,
                  trackClicked: {
                    name: 'Find a Job',
                    properties: { context: 'Header' },
                  },
                }
              : { type: 'inert' }
          }
          variant="grey"
          qaId="page-header-find-a-job-dropdown-trigger"
        >
          {text} <DropdownChevron />
        </LayoutHeaderButton>
      }
      desktopTriggersOnHover
    >
      <DropdownFoldMenu
        left={{
          title: search.title,
          items: search.items.map((searchItem) => {
            const { iconName, qaId, trackClickedName } =
              findAJobDropdownDataBySearchItemType[searchItem.type];

            return {
              iconName,
              title: searchItem.title,
              href: searchItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Find a Job',
              },
            };
          }),
        }}
        right={{
          title: resources.title,
          items: resources.items.map((resourceItem) => {
            const { image, imageHover, qaId, trackClickedName } =
              findAJobDropdownDataByResourcesItemType[resourceItem.type];

            return {
              image,
              imageHover,
              title: resourceItem.title,
              description: resourceItem.description,
              href: resourceItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Find a Job',
              },
            };
          }),
        }}
      />
    </Dropdown>
  );
}
